import {
  type Canvas,
  FabricImage,
  FabricText,
  Group,
  Rect,
  Shadow,
} from "fabric";
import type { TaggedFabricObject } from "./constants";

// --- Geometry ---

export function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

export function getNormalizedPoint(p: { x: number; y: number }) {
  return { x: clamp(p.x, 0, 100), y: clamp(p.y, 0, 100) };
}

interface ImageNaturalSize {
  width: number;
  height: number;
}

export function clampToImageBounds(
  p: { x: number; y: number },
  bgObj: FabricImage,
  naturalSize: ImageNaturalSize,
): { x: number; y: number } {
  const scale = bgObj.scaleX!;
  const scaledW = naturalSize.width * scale;
  const scaledH = naturalSize.height * scale;
  const tlX = bgObj.left! - scaledW / 2;
  const tlY = bgObj.top! - scaledH / 2;
  return {
    x: clamp(p.x, tlX, tlX + scaledW),
    y: clamp(p.y, tlY, tlY + scaledH),
  };
}

// --- Canvas image accessors ---

export function getBgImage(canvas: Canvas): FabricImage | null {
  return (
    (canvas.getObjects().find((o) => (o as TaggedFabricObject).__isFloorPlanBg === true) as FabricImage) ?? null
  );
}

export function getImageNaturalSize(canvas: Canvas): ImageNaturalSize | null {
  const bg = getBgImage(canvas);
  if (!bg) return null;
  return { width: bg.width!, height: bg.height! };
}

export function pixelToPercentStatic(
  p: { x: number; y: number },
  canvas: Canvas,
): { x: number; y: number } {
  const bg = getBgImage(canvas);
  const ns = getImageNaturalSize(canvas);
  if (!bg || !ns) return { x: 0, y: 0 };
  const scale = bg.scaleX!;
  const tlX = bg.left! - (ns.width * scale) / 2;
  const tlY = bg.top! - (ns.height * scale) / 2;
  return {
    x: ((p.x - tlX) / (scale * ns.width)) * 100,
    y: ((p.y - tlY) / (scale * ns.height)) * 100,
  };
}

// --- Label ---

export function createLabelGroup(
  text: string,
  px: number,
  py: number,
  options: {
    originX?: string;
    originY?: string;
    fontSize?: number;
    fontFamily?: string;
    fontWeight?: number;
    color?: string;
    backgroundColor?: string;
    stroke?: string;
    strokeWidth?: number;
    radius?: number;
    paddingH?: number;
    paddingV?: number;
    shadow?: string;
  } = {},
): Group {
  const fontSize = options.fontSize ?? 12;
  const fontFamily = options.fontFamily ?? "Pretendard Variable, Pretendard, sans-serif";
  const fw = options.fontWeight ?? 600;
  const color = options.color ?? "#2C2D2E";
  const bgColor = options.backgroundColor ?? "#FFFFFF";
  const padH = options.paddingH ?? 6;
  const padV = options.paddingV ?? 4;
  const radius = options.radius ?? 1;

  const textObj = new FabricText(text, {
    fontSize,
    fontFamily,
    fontWeight: fw,
    fill: color,
    backgroundColor: "rgba(0,0,0,0)",
    textAlign: "center",
    originX: "center",
    originY: "center",
    objectCaching: false,
  });

  const textW = textObj.width ?? 0;
  const textH = textObj.height ?? 0;
  const bgW = textW + padH * 2;
  const bgH = textH + padV * 2;
  const r = Math.min(radius, bgH / 2);

  const bg = new Rect({
    originX: "center",
    originY: "center",
    width: bgW,
    height: bgH,
    fill: bgColor,
    rx: r,
    ry: r,
    stroke: options.stroke,
    strokeWidth: options.strokeWidth ?? 0,
    shadow: options.shadow ? new Shadow(options.shadow) : undefined,
    objectCaching: false,
  });

  return new Group([bg, textObj], {
    left: px,
    top: py,
    originX: "center",
    originY: "center",
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

/**
 * 라벨/스트로크 시각 크기를 bg 이미지의 displayed width 에 비례하게 계산 (PO QA 2차 r ①).
 * 같은 캔버스 크기여도 bg 이미지 natural 사이즈가 달라 displayed scale 이 달라지면 view 면적 대비
 * 라벨 비율이 달라 보이는 문제 보정. snapshot/floorplan 둘 다 동일 helper 사용해 비율 통일.
 *
 * 기준점: bg displayed width 443 → fontSize 12 (FullHD 1920 가 800 캔버스에 0.8 fit 일 때)
 */
export function getDynamicLineLabelStyle(canvas: import("fabric").Canvas | null): {
  fontSize: number;
  paddingH: number;
  paddingV: number;
} {
  const BASE_FONT_SIZE = 12;
  const BASE_PADDING_H = 8;
  const BASE_PADDING_V = 4;
  const objs = canvas?.getObjects() ?? [];
  const fpBg = objs.find((o) => (o as { __isFloorPlanBg?: boolean }).__isFloorPlanBg);
  // 스냅샷은 기존 fontSize 12 유지. 도면만 displayed width 비례 축소 (view 면적 대비 비율 통일).
  if (!fpBg) return { fontSize: BASE_FONT_SIZE, paddingH: BASE_PADDING_H, paddingV: BASE_PADDING_V };
  const w = (fpBg as { width?: number; scaleX?: number }).width;
  const s = (fpBg as { width?: number; scaleX?: number }).scaleX;
  if (!w || !s) return { fontSize: BASE_FONT_SIZE, paddingH: BASE_PADDING_H, paddingV: BASE_PADDING_V };
  const displayedW = w * s;
  // 도면 라벨은 6~12 사이에서 displayed width 비례. SCALAR 0.018.
  const fontSize = Math.max(6, Math.min(12, Math.round(displayedW * 0.018)));
  // (F12) padding 도 fontSize 에 비례 스케일 — 스냅샷 scaleLabelStyle 과 동일 방식
  const effScale = fontSize / BASE_FONT_SIZE;
  return {
    fontSize,
    paddingH: Math.max(2, BASE_PADDING_H * effScale),
    paddingV: Math.max(2, BASE_PADDING_V * effScale),
  };
}

/**
 * 라인 라벨이 라인을 가릴 때 캔버스 Y 축으로 위로 밀어 올리는 offset 계산 (PO QA 2차 r ②).
 *
 * 핵심 설계:
 * - 방향(perpendicular) 미사용 → 세로/대각 라인이 미세하게 방향이 바뀌어도 좌우로 튀지 않음.
 * - 항상 캔버스 Y 위쪽으로만 이동 → 가로/세로 라인 모두 시각적으로 라벨이 위에 위치 (자연스러움).
 * - 라인 길이가 라벨 너비보다 짧을수록 t (0~1) 가 1 에 가까워지며 점진적으로 위로 밀려남.
 *   transitionRange (px) 폭에서 t 가 부드럽게 변해 임계값 부근에서도 "덜컹" 없음.
 *
 * @param lineLen 라인 길이 (px, world)
 * @param labelW 라벨 너비 (px)
 * @param labelH 라벨 높이 (px)
 * @returns 라인 그룹 중심 기준 라벨이 가질 top offset (음수 = 위쪽)
 */
export function computeLineLabelTopOffset(
  lineLen: number,
  labelW: number,
  labelH: number,
): number {
  if (labelW <= 0 || labelH <= 0) return 0;
  const threshold = labelW * 0.9;
  const transitionRange = 20;
  const t = Math.max(0, Math.min(1, (threshold + transitionRange - lineLen) / transitionRange));
  return -t * (labelH / 2 + 6);
}
